/**
 * LessonPackCard — a pack the member holds.
 *
 * Presentational only; strings arrive pre-formatted (`@butter/library/lessons`
 * + `lessonPackDisplay`). The one web holdings card: `/plans` (current +
 * history) and the purchase-success page.
 *
 * @module pages/lessons/components/LessonPackCard
 */

import { Card } from '@butter/ui/primitives/data-display';
import { MembershipCardGradient } from '@butter/ui/domains/subscriptions';

export interface LessonPackCardProps {
  /** The pack's id — drives default testids. */
  packId: number;
  name: string;
  description?: string | null;
  /** The TEMPLATE's color — the package's own color, never the tenant theme. */
  colorName: string | null | undefined;
  statusLabel: string;
  /** Footer figure, verbatim: '3.3 Hours left' on /plans, '5 Hours' on the success page. */
  figureLabel: string;
  purchasedLabel: string;
  /** A tombstoned template still renders its name; no purchase CTA anywhere on this card. */
  isTombstoned?: boolean;
  /** Density switch for the history list: drops the description, shrinks the date. No domain branching. */
  compact?: boolean;
  /** Heading level for the pack name — keep each page's outline. @default 'h3' */
  titleAs?: 'h2' | 'h3';
  /** Overrides the card's root testid (the success page keeps `lesson-success-card`). @default `lessons-pack-card-${packId}` */
  'data-testid'?: string;
}

export function LessonPackCard({
  packId,
  name,
  description,
  colorName,
  statusLabel,
  figureLabel,
  purchasedLabel,
  isTombstoned = false,
  compact = false,
  titleAs = 'h3',
  'data-testid': testId,
}: LessonPackCardProps) {
  return (
    <Card
      as="article"
      rounded="3xl"
      className="flex flex-col gap-5 overflow-hidden rounded-28 border border-butter-gray-100 bg-white p-2 pb-3"
      bordered={false}
      shadow={false}
      data-testid={testId ?? `lessons-pack-card-${packId}`}
    >
      <MembershipCardGradient
        size="lg"
        showPrice={false}
        titleAs={titleAs}
        membership={{ name, description: compact ? null : description, color: colorName }}
        badge={<span data-testid={`lessons-pack-status-${packId}`}>{statusLabel}</span>}
      />
      <div className="flex flex-col items-start gap-1 px-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <p
          className="text-28 font-semibold text-fg-primary"
          data-testid={`lessons-pack-remaining-${packId}`}
        >
          {figureLabel}
        </p>
        <p className={compact ? 'text-sm font-semibold text-fg-secondary' : 'text-lg font-semibold text-fg-secondary'}>
          Purchased: {purchasedLabel}
        </p>
      </div>
      {isTombstoned ? (
        <p className="px-6 text-sm font-medium text-fg-quaternary" data-testid={`lessons-pack-tombstone-${packId}`}>
          No longer sold
        </p>
      ) : null}
    </Card>
  );
}
